import React, { useEffect } from "react";
import Swal from "sweetalert2";
import {
  useLazyGetEventRsvpsQuery,
  useCancelRsvpMutation,
} from "../api/businessApi";

const EventRsvpList = ({ event, onBack }) => {
  const [getRsvps, { data: rsvpsData, isLoading, error }] =
    useLazyGetEventRsvpsQuery();
  const [cancelRsvp, { isLoading: isCancelling }] = useCancelRsvpMutation();

  useEffect(() => {
    if (event?.id) {
      getRsvps(event.id);
    }
  }, [event?.id, getRsvps]);

  const handleCancelRsvp = async (rsvp) => {
    const result = await Swal.fire({
      title: "Cancel RSVP?",
      text: `Cancel RSVP for "${rsvp.name}"?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#dc2626",
      cancelButtonColor: "#6b7280",
      confirmButtonText: "Yes, cancel it",
      cancelButtonText: "No, keep it",
    });

    if (result.isConfirmed) {
      try {
        await cancelRsvp({ eventId: event.id, rsvpId: rsvp.id }).unwrap();
        Swal.fire({
          title: "Cancelled!",
          text: "RSVP has been cancelled.",
          icon: "success",
          timer: 2000,
          showConfirmButton: false,
        });
        getRsvps(event.id);
      } catch (err) {
        Swal.fire({
          title: "Error!",
          text: err?.data?.message || err?.message || "Failed to cancel RSVP",
          icon: "error",
          confirmButtonColor: "#dc2626",
        });
      }
    }
  };

  const rsvps = rsvpsData?.rsvps || rsvpsData || [];

  const registeredCount = rsvps.filter(
    (r) => r.status === "registered" || r.status === "confirmed"
  ).length;
  const waitlistedCount = rsvps.filter(
    (r) => r.status === "waitlisted"
  ).length;
  const cancelledCount = rsvps.filter(
    (r) => r.status === "cancelled"
  ).length;

  const getStatusBadge = (status) => {
    const map = {
      registered: "bg-green-100 text-green-800",
      confirmed: "bg-green-100 text-green-800",
      waitlisted: "bg-yellow-100 text-yellow-800",
      cancelled: "bg-red-100 text-red-800",
    };
    return map[status] || "bg-gray-100 text-gray-800";
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "N/A";
    return new Date(dateStr).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  };

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <button
            onClick={onBack}
            className="text-emerald-600 hover:text-emerald-800 text-sm font-medium flex items-center gap-1 mb-2"
          >
            <svg
              className="w-4 h-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M10 19l-7-7m0 0l7-7m-7 7h18"
              />
            </svg>
            Back to Events
          </button>
          <h2 className="text-2xl font-bold text-gray-900">
            RSVPs for: {event.title}
          </h2>
        </div>
      </div>

      {/* Stats Summary */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <div className="text-2xl font-bold text-green-700">
            {registeredCount}
          </div>
          <div className="text-sm text-green-600">Registered</div>
        </div>
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <div className="text-2xl font-bold text-yellow-700">
            {waitlistedCount}
          </div>
          <div className="text-sm text-yellow-600">Waitlisted</div>
        </div>
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="text-2xl font-bold text-red-700">
            {cancelledCount}
          </div>
          <div className="text-sm text-red-600">Cancelled</div>
        </div>
      </div>

      {isLoading && (
        <div className="flex justify-center items-center h-32">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600"></div>
        </div>
      )}

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <div className="text-red-700">
            Error loading RSVPs: {error.message || "Unknown error"}
          </div>
        </div>
      )}

      {!isLoading && !error && (
        <div className="bg-white shadow rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Name
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Email
                  </th>
                  <th className="hidden sm:table-cell px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Phone
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="hidden sm:table-cell px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Date
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {rsvps.map((rsvp) => (
                  <tr key={rsvp.id}>
                    <td className="px-6 py-4 text-sm font-medium text-gray-900">
                      {rsvp.name}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900">
                      {rsvp.email}
                    </td>
                    <td className="hidden sm:table-cell px-6 py-4 text-sm text-gray-900">
                      {rsvp.phone || "-"}
                    </td>
                    <td className="px-6 py-4">
                      <span
                        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusBadge(
                          rsvp.status
                        )}`}
                      >
                        {rsvp.status || "registered"}
                      </span>
                    </td>
                    <td className="hidden sm:table-cell px-6 py-4 text-sm text-gray-900">
                      {formatDate(rsvp.createdAt || rsvp.created_at)}
                    </td>
                    <td className="px-6 py-4 text-sm">
                      {rsvp.status !== "cancelled" && (
                        <button
                          onClick={() => handleCancelRsvp(rsvp)}
                          disabled={isCancelling}
                          className="text-red-600 hover:text-red-900 font-medium flex items-center gap-1"
                        >
                          <svg
                            className="w-4 h-4"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth="2"
                              d="M6 18L18 6M6 6l12 12"
                            />
                          </svg>
                          Cancel
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
                {rsvps.length === 0 && (
                  <tr>
                    <td
                      colSpan="6"
                      className="px-6 py-8 text-center text-gray-500"
                    >
                      No RSVPs yet
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default EventRsvpList;
